import { LegendConfig } from '../components/charts/MapLegend';

// Map variable configurations for different data types
export interface MapVariableConfig {
  id: string;
  name: string;
  description: string;
  dataSource: string;
  legendConfig: LegendConfig;
  colorFunction: (value: number, quintileBreaks?: number[]) => string;
}

// Income map configuration
export const incomeMapConfig: MapVariableConfig = {
  id: 'income',
  name: 'Median Household Income',
  description: 'Median household income by census tract',
  dataSource: 'acs5_income',
  legendConfig: {
    title: 'Median Household Income',
    dataYear: '2023 Data',
    // 10-class green (low) to red (high)
    colorScale: ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'],
    breaks: [],
    unit: '$',
    tractCount: 0, // Will be set dynamically
    referenceValue: { label: 'California Median', value: 95521 }
  },
  colorFunction: (income: number, decileBreaks?: number[]): string => {
    const palette = ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'];
    if (!decileBreaks || decileBreaks.length === 0) return palette[0];
    const idx = decileBreaks.findIndex((b) => income < b);
    return palette[idx >= 0 ? idx : palette.length - 1];
  }
};



// Example configuration for housing costs (for future use)
export const housingCostConfig: MapVariableConfig = {
  id: 'housing_cost',
  name: 'Median Housing Cost',
  description: 'Median housing cost by census tract',
  dataSource: 'housing_data',
  legendConfig: {
    title: 'Median Housing Cost',
    dataYear: '2023 Data',
    colorScale: ['#2166ac', '#4393c3', '#92c5de', '#d1e5f0', '#f7f7f7', '#fddbc7', '#f4a582', '#d6604d', '#b2182b'],
    breaks: [0, 200000, 300000, 400000, 500000, 600000, 700000, 800000, 1000000],
    unit: '$',
    tractCount: 0,
    referenceValue: { label: 'California Median', value: 550000 }
  },
  colorFunction: (cost: number): string => {
    if (cost < 200000) return '#2166ac';
    if (cost < 300000) return '#4393c3';
    if (cost < 400000) return '#92c5de';
    if (cost < 500000) return '#d1e5f0';
    if (cost < 600000) return '#f7f7f7';
    if (cost < 700000) return '#fddbc7';
    if (cost < 800000) return '#f4a582';
    if (cost < 1000000) return '#d6604d';
    return '#b2182b';
  }
};

// EV adoption configuration
export const evAdoptionConfig: MapVariableConfig = {
  id: 'ev_adoption',
  name: 'Electric Vehicles per 100 People',
  description: 'Number of electric vehicles per 100 people by census tract',
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: 'Electric Vehicles per 100 People',
    dataYear: '2023 Data',
    colorScale: ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'],
    breaks: [],
    unit: '',
    tractCount: 0,
    referenceValue: { label: 'California Average', value: 2.1 }
  },
  colorFunction: (evs: number, decileBreaks?: number[]): string => {
    const palette = ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'];
    if (!decileBreaks || decileBreaks.length === 0) return palette[0];
    const idx = decileBreaks.findIndex((b) => evs < b);
    return palette[idx >= 0 ? idx : palette.length - 1];
  }
};

// Vehicles per household configuration
export const vehiclesPerHouseholdConfig: MapVariableConfig = {
  id: 'vehicles_per_household',
  name: 'Vehicles per Household',
  description: 'Average number of vehicles per household by census tract',
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: 'Vehicles per Household',
    dataYear: '2023 Data',
    colorScale: ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'],
    breaks: [],
    unit: '',
    tractCount: 0,
    referenceValue: { label: 'California Average', value: 2.1 }
  },
  colorFunction: (vehicles: number, decileBreaks?: number[]): string => {
    const palette = ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'];
    if (!decileBreaks || decileBreaks.length === 0) return palette[0];
    const idx = decileBreaks.findIndex((b) => vehicles < b);
    return palette[idx >= 0 ? idx : palette.length - 1];
  }
};

// Total chargers configuration
export const totalChargersConfig: MapVariableConfig = {
  id: 'total_chargers',
  name: 'Total EV Chargers',
  description: 'Total number of EV chargers by census tract',
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: 'Total EV Chargers',
    dataYear: '2023 Data',
    colorScale: ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'],
    breaks: [],
    unit: '',
    tractCount: 0,
    referenceValue: { label: 'California Average', value: 5.2 }
  },
  colorFunction: (chargers: number, decileBreaks?: number[]): string => {
    const palette = ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'];
    if (!decileBreaks || decileBreaks.length === 0) return palette[0];
    const idx = decileBreaks.findIndex((b) => chargers < b);
    return palette[idx >= 0 ? idx : palette.length - 1];
  }
};

// Population density configuration
export const populationDensityConfig: MapVariableConfig = {
  id: 'population_density',
  name: 'Population Density',
  description: 'Population density in persons per square mile by census tract',
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: 'Population Density',
    dataYear: '2023 Data',
    colorScale: ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'],
    breaks: [],
    unit: ' per sq mi',
    tractCount: 0,
    referenceValue: { label: 'California Average', value: 2500 }
  },
  colorFunction: (density: number, decileBreaks?: number[]): string => {
    const palette = ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'];
    if (!decileBreaks || decileBreaks.length === 0) return palette[0];
    const idx = decileBreaks.findIndex((b) => density < b);
    return palette[idx >= 0 ? idx : palette.length - 1];
  }
};

// Political lean configuration with deciles
export const politicalLeanConfig: MapVariableConfig = {
  id: 'political_lean',
  name: 'Political Lean',
  description: 'Democratic minus Republican voter percentage by census tract',
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: 'Political Lean',
    dataYear: '2023 Data',
    colorScale: ['#8B0000', '#DC143C', '#FF6347', '#FFA07A', '#FFE4E1', '#E6F3FF', '#87CEEB', '#4169E1', '#0000CD', '#000080'],
    breaks: [], // Will be calculated dynamically based on deciles
    unit: '%',
    tractCount: 0,
    referenceValue: { label: 'California Average', value: 15 }
  },
  colorFunction: (lean: number, decileBreaks?: number[]): string => {
    // Default breaks if decileBreaks not provided (fallback)
    const breaks = decileBreaks || [-50, -40, -30, -20, -10, 0, 10, 20, 30, 40, 50];
    
    if (lean < breaks[0]) return '#8B0000'; // Most Republican - dark red
    if (lean < breaks[1]) return '#DC143C'; // Very Republican - crimson
    if (lean < breaks[2]) return '#FF6347'; // Strong Republican - tomato
    if (lean < breaks[3]) return '#FFA07A'; // Lean Republican - light salmon
    if (lean < breaks[4]) return '#FFE4E1'; // Slight Republican - misty rose
    if (lean < breaks[5]) return '#E6F3FF'; // Slight Democratic - light blue
    if (lean < breaks[6]) return '#87CEEB'; // Lean Democratic - sky blue
    if (lean < breaks[7]) return '#4169E1'; // Strong Democratic - royal blue
    if (lean < breaks[8]) return '#0000CD'; // Very Democratic - medium blue
    return '#000080'; // Most Democratic - navy blue
  }
};

// Carbon footprint configuration
export const carbonFootprintConfig: MapVariableConfig = {
  id: 'carbon_footprint',
  name: 'Average Household Carbon Footprint',
  description: 'Average household carbon footprint by census tract',
  dataSource: 'cbei_tracts',
  legendConfig: {
    title: 'Average Household Carbon Footprint',
    dataYear: '2023 Data',
    colorScale: ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'],
    breaks: [],
    unit: ' MT CO₂e/HH',
    tractCount: 0,
    referenceValue: { label: 'California Average', value: 45.2 }
  },
  colorFunction: (footprint: number, decileBreaks?: number[]): string => {
    const palette = ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'];
    if (!decileBreaks || decileBreaks.length === 0) return palette[0];
    const idx = decileBreaks.findIndex((b) => footprint < b);
    return palette[idx >= 0 ? idx : palette.length - 1];
  }
};

// All available map configurations (in alphabetical order)
export const mapConfigs: MapVariableConfig[] = [
  carbonFootprintConfig,
  evAdoptionConfig,
  incomeMapConfig,
  politicalLeanConfig,
  populationDensityConfig,
  totalChargersConfig,
  vehiclesPerHouseholdConfig
  // Note: housingCostConfig removed - no data available
];

// Helper function to get config by ID
export const getMapConfig = (id: string): MapVariableConfig | undefined => {
  return mapConfigs.find(config => config.id === id);
};
